import Link from 'next/link';
import { BsPencil } from 'react-icons/bs';
import { IoBookmarkOutline, IoBriefcaseOutline, IoCubeOutline, IoDocumentTextOutline } from 'react-icons/io5';

import { Anchor, SimpleGrid, Stack, Text } from '@mantine/core';

import { Component, SavedItem, SavedItemType } from 'models';
import { CollateralHelpers } from 'helpers/CollateralHelpers';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { DesignLibraryHelpers } from 'helpers/DesignLibraryHelpers';
import { ProjectHelpers } from 'helpers/ProjectHelpers';
import { SavedItemsHelpers } from 'helpers/SavedItemsHelpers';

import { CompanyLogo } from 'components/company-logo/CompanyLogo';
import { CompanyLogoFallback } from 'components/company-logo/CompanyLogoFallback';
import { DashboardPanel } from 'components/dashboard-panel/DashboardPanel';
import cxPanel from 'components/dashboard-panel/DashboardPanel.module.scss';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useCollateralItem } from 'hooks/use-collateral-item';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useComponent } from 'hooks/use-component';
import { useComponentMeta } from 'hooks/use-component-meta';
import { useCurrentSavedItems } from 'hooks/use-current-saved-items';
import { useProject } from 'hooks/use-project';
import { useSnapshot } from 'hooks/use-safe-snapshot';
import { savedItemsState } from 'state/current-saved-items';

import { DashboardActionRow, DashboardActionRowSkeleton, THUMBNAIL_SIZE } from './DashboardActionRow';

const NB_SHOWN_SAVED_ITEMS = 6;

const DashboardSaved = () => {
    const { components, profiles, designs, collateral } = useCurrentSavedItems();
    const { initialized } = useSnapshot(savedItemsState);

    const latestSavedItems = [...components, ...profiles, ...designs, ...collateral]
        .sort((first, second) => new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime())
        .slice(0, NB_SHOWN_SAVED_ITEMS);

    return (
        <DashboardPanel
            icon={<IoBookmarkOutline />}
            color="yellow"
            title="Saved"
            rightSection={
                latestSavedItems.length > 0 && (
                    <Anchor component={Link} href={SavedItemsHelpers.urls.overview()} fw={500} fz="sm">
                        View All
                    </Anchor>
                )
            }
        >
            {!initialized ? (
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing={0} className={cxPanel.dividedGrid}>
                    {Array.from({ length: NB_SHOWN_SAVED_ITEMS }, (_, index) => (
                        <DashboardActionRowSkeleton key={index} />
                    ))}
                </SimpleGrid>
            ) : latestSavedItems.length ? (
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing={0} className={cxPanel.dividedGrid}>
                    {latestSavedItems.map((savedItem) => (
                        <SavedItemRow key={savedItem.id} savedItem={savedItem} />
                    ))}
                </SimpleGrid>
            ) : (
                <DashboardSavedEmpty />
            )}
        </DashboardPanel>
    );
};

const SAVED_SOURCES = [
    {
        icon: <IoCubeOutline />,
        title: 'Browse products',
        description: 'Components from leading manufacturers',
        href: ComponentHelpers.urls.overview(),
    },
    {
        icon: <IoBriefcaseOutline />,
        title: 'Find profiles',
        description: 'Manufacturers, integrators, and installers',
        href: CompanyHelpers.urls.overview(),
    },
    {
        icon: <BsPencil />,
        title: 'Explore reference designs',
        description: 'Explore handpicked reference designs created by DC engineers',
        href: DesignLibraryHelpers.urls.overview(),
    },
];

const DashboardSavedEmpty = () => (
    <>
        <Text size="sm" c="dimmed">
            Nothing saved yet. Bookmark products, profiles, and designs to find them here.
        </Text>

        <Stack gap={0} className={cxPanel.dividedList}>
            {SAVED_SOURCES.map((source) => (
                <DashboardActionRow
                    key={source.title}
                    href={source.href}
                    icon={source.icon}
                    color="yellow"
                    title={source.title}
                    description={source.description}
                />
            ))}
        </Stack>
    </>
);

const SavedItemRow = ({ savedItem }: { savedItem: SavedItem }) => {
    const { relationTo, value } = savedItem.item;

    switch (relationTo) {
        case SavedItemType.COMPONENT:
            return <SavedProduct productId={value} />;
        case SavedItemType.PROFILE:
            return <SavedProfile profileId={value} />;
        case SavedItemType.PROJECT:
            return <SavedDesign projectId={value} />;
        case SavedItemType.COLLATERAL:
            return <SavedCollateral collateralId={value} />;
        default:
            return null;
    }
};

const SavedProduct = ({ productId }: { productId: string }) => {
    const { component, isLoading } = useComponent(productId);

    if (isLoading) {
        return <DashboardActionRowSkeleton />;
    }

    return component ? <SavedProductRow component={component} /> : null;
};

const SavedProductRow = ({ component }: { component: Component }) => {
    const { uniqueName, slug } = useComponentMeta(component);

    return (
        <DashboardActionRow
            href={ComponentHelpers.urls.view(component.id, slug)}
            thumbnail={
                <ComponentThumbnail
                    component={component}
                    showEmpty
                    style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
                />
            }
            title={uniqueName}
            description="Product"
        />
    );
};

const SavedProfile = ({ profileId }: { profileId: string }) => {
    const { company, isLoading } = useCompanyProfile(profileId);

    if (isLoading) {
        return <DashboardActionRowSkeleton />;
    }

    if (!company) {
        return null;
    }

    return (
        <DashboardActionRow
            href={CompanyHelpers.urls.view(company.slug)}
            thumbnail={
                <CompanyLogo
                    logos={company.logos}
                    width={THUMBNAIL_SIZE}
                    fallback={<CompanyLogoFallback name={company.name} w={THUMBNAIL_SIZE} h={THUMBNAIL_SIZE} />}
                />
            }
            title={company.name}
            description="Profile"
        />
    );
};

const SavedDesign = ({ projectId }: { projectId: string }) => {
    const { project, isLoading } = useProject(projectId);

    if (isLoading) {
        return <DashboardActionRowSkeleton />;
    }

    if (!project) {
        return null;
    }

    return (
        <DashboardActionRow
            href={ProjectHelpers.urls.editor(project.id)}
            icon={<BsPencil />}
            color="blue"
            title={project.name}
            description="Design"
        />
    );
};

const SavedCollateral = ({ collateralId }: { collateralId: string }) => {
    const { collateral, href, isLoading } = useCollateralItem(collateralId);

    if (isLoading) {
        return <DashboardActionRowSkeleton />;
    }

    if (!collateral || !href) {
        return null;
    }

    return (
        <DashboardActionRow
            href={href}
            icon={<IoDocumentTextOutline />}
            color="orange"
            title={collateral.title}
            description={CollateralHelpers.categoryLabel(collateral)}
        />
    );
};

export { DashboardSaved };
